import { ReactNode, createContext, useContext, useEffect, useMemo } from 'react'

import { useDebouncedValue } from '@shared/hooks/useDebouncedValue'
import { useNotification } from '@shared/hooks/useNotification'
import { useQuery } from '@tanstack/react-query'

import authApi from '../api'
import { getIsAuthenticated } from '../utils'
import { AuthContextType } from './types'

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isFetching: true,
})

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { showNotification } = useNotification()

  const isAuthorised = getIsAuthenticated()

  const {
    data: { data: rawUser } = {},
    isFetching: rawIsFetching = true,
    status,
  } = useQuery({ queryKey: [authApi.getMe.key], queryFn: () => authApi.getMe.fn(), enabled: !!isAuthorised, retry: false })

  useEffect(() => {
    if (status === 'error') {
      showNotification('Something went wrong', 'error')
    }
  }, [status, showNotification])

  // a hotfix to let data propagate before redirect
  const isFetching = useDebouncedValue(rawIsFetching, 200)

  const isAuthenticated = !!rawUser

  const user = useMemo(() => {
    if (!rawUser) {
      return rawUser
    }

    const events = rawUser?.events.slice().sort((a, b) => (a.startDate > b.startDate ? -1 : 1))
    const attendEvents = events.filter(e => e.isAttending).reduce((acc, e) => ({ ...acc, [e.id]: e }), {})

    return {
      ...rawUser,
      events,
      attendEvents,
    }
  }, [rawUser])

  const contextValue = useMemo(() => ({ isAuthenticated, user, isFetching }), [isAuthenticated, user, isFetching])

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export const useAuthContext = () => {
  const authContext = useContext(AuthContext)

  if (!authContext) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }

  return authContext
}
